import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Phone, Mail, FolderOpen, Folder, File as FileIcon, Upload, Download, X, LayoutGrid, List as ListIcon, LayoutList, ChevronRight, FolderPlus, MoreVertical, MoveRight, Calendar, PhoneCall, MailPlus, MessageSquare, Cake, Camera, Plus, CheckCircle2, Archive, Eye, EyeOff, Clock, Award, HeartHandshake, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/hooks/useConfirm";
import { useModalBack } from "@/hooks/useModalBack";
import EmployeeDetailsDialog from "@/components/dashboard/EmployeeDetailsDialog";
import AdminCertificatesDialog from "@/components/dashboard/AdminCertificatesDialog";
import BenefitsDialog from "@/components/dashboard/BenefitsDialog";
import PhotoCropDialog from "@/components/dashboard/PhotoCropDialog";
import VacationWaiverDialog from "@/components/dashboard/VacationWaiverDialog";
import { useAuth } from "@/context/AuthContext";

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);


const daysUntil = (s) => {
  if (!s) return null;
  const iso = s.length === 10 ? `${s}T00:00:00` : s;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date()) / 86400000);
};

/** Whole years between the given ISO date (YYYY-MM-DD) and today. */
const ageFrom = (dob) => {
  if (!dob) return null;
  const iso = dob.length === 10 ? `${dob}T00:00:00` : dob;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const beforeBirthday =
    now.getMonth() < d.getMonth() ||
    (now.getMonth() === d.getMonth() && now.getDate() < d.getDate());
  if (beforeBirthday) years -= 1;
  return years >= 0 ? years : null;
};

/** Returns [{label, days}] for the employee's license + certs that expire in ≤60 days
 *  (or are already expired). Sorted soonest first. */
const expiringLabels = (e) => {
  const items = [];
  const push = (label, dt) => {
    const d = daysUntil(dt);
    if (d !== null && d <= 60) items.push({ label, days: d });
  };
  push("License", e?.license_expiry);
  (e?.certifications || []).forEach((c) => {
    if (c?.name) push(c.name, c?.expiry);
  });
  items.sort((a, b) => a.days - b.days);
  return items;
};

const tenure = (s) => {
  if (!s) return "";
  const iso = s.length === 10 ? `${s}T00:00:00` : s;
  const start = new Date(iso);
  if (isNaN(start.getTime())) return "";
  const now = new Date();
  if (start > now) return "future hire";  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();
  if (days < 0) {
    months -= 1;
    // number of days in the month prior to `now` (handles month rollover)
    const prevMonthLastDay = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    days += prevMonthLastDay;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  const parts = [];
  if (years > 0) parts.push(`${years} yr${years === 1 ? "" : "s"}`);
  if (months > 0) parts.push(`${months} mo`);
  if (days > 0 && years === 0) parts.push(`${days} day${days === 1 ? "" : "s"}`);
  if (parts.length === 0) return "just hired";
  return parts.join(", ");
};

/** Whole years since hire — drives the 5-year service badge (matches the
 * backend's automatic vacation bump from 4% to 6% at 5 years; owners are
 * vacation-exempt so their badge is a service milestone only). */
const tenureFullYears = (s) => {
  if (!s) return 0;
  const iso = s.length === 10 ? `${s}T00:00:00` : s;
  const start = new Date(iso);
  if (isNaN(start.getTime())) return 0;
  const now = new Date();
  if (start > now) return 0;
  let years = now.getFullYear() - start.getFullYear();
  if (now.getMonth() < start.getMonth() ||
      (now.getMonth() === start.getMonth() && now.getDate() < start.getDate())) years -= 1;
  return Math.max(0, years);
};

/** The date an employee hits 5 years of service (null if no/invalid hire
 * date) — used to flag upcoming 4%→6% vacation bumps a year ahead. */
const fiveYearDate = (s) => {
  if (!s) return null;
  const iso = s.length === 10 ? `${s}T00:00:00` : s;
  const start = new Date(iso);
  if (isNaN(start.getTime())) return null;
  return new Date(start.getFullYear() + 5, start.getMonth(), start.getDate());
};


/** Date input with a small × button that clears the value when set. */
const DateInput = ({ id, value, onChange, onClear, testid, clearTestid }) => (
  <div className="relative mt-1">
    <Input
      id={id}
      type="date"
      data-testid={testid}
      value={value || ""}
      onChange={onChange}
      className="border-white/15 bg-white/5 pr-9 text-white [color-scheme:dark]"
    />
    {value ? (
      <button
        type="button"
        onClick={onClear}
        data-testid={clearTestid}
        aria-label="Clear date"
        title="Clear date"
        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-white/50 hover:bg-white/10 hover:text-red-400"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    ) : null}
  </div>
);

const statusStyle = (s) =>
  s === "Active"
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
    : "bg-white/10 text-white/50 border-white/20";

/** Circular avatar showing employee.photo or initials fallback. */
const EmployeeAvatar = ({ employee, size = 44, testid }) => {
  const initials = (employee?.name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
  const style = { width: size, height: size };
  if (employee?.photo) {
    return (
      <img
        src={employee.photo}
        alt={employee.name || ""}
        data-testid={testid}
        style={style}
        className="shrink-0 rounded-full object-cover ring-1 ring-white/10"
      />
    );
  }
  return (
    <div
      data-testid={testid}
      style={style}
      className="flex shrink-0 items-center justify-center rounded-full bg-white/[0.05] font-semibold text-white/70 ring-1 ring-white/10"
    >
      {initials || "?"}
    </div>
  );
};

const FIELDS = [
  ["name", "Full name"],
  ["trade", "Trade / role"],
  ["job_title", "Job title"],
  ["phone", "Phone"],
  ["email", "Email"],
  ["status", "Status"],
];

const emptyForm = {
  name: "",
  trade: "",
  job_title: "",
  phone: "",
  email: "",
  pay_type: "hourly",
  hourly_rate: "",
  hourly_rate_perfect: "",
  _showPerfectRate: false,
  weekly_salary: "",
  overtime_exempt: false,
  vacation_opt_out: false,
  vacation_waiver: null,
  linked_role: null,
  time_clock_enabled: false,
  paid_lunch_minutes: 30,
  employment_type: "full_time",
  hired_at: "",
  address_street: "",
  address_city: "",
  address_postal: "",
  date_of_birth: "",
  emergency_contact_name: "",
  emergency_contact_relationship: "",
  emergency_contact_phone: "",
  emergency_contact_2_name: "",
  emergency_contact_2_relationship: "",
  emergency_contact_2_phone: "",
  sin: "",
  license_number: "",
  license_expiry: "",
  certifications: [],
  allergies: "",
  shirt_size: "",
  bank_transit: "",
  bank_institution: "",
  bank_account: "",
  photo: "",
  status: "Active",
  notes: "",
};

const EmployeesTab = () => {
  const [employees, setEmployees] = useState([]);
  const [statusView, setStatusView] = useState(() => localStorage.getItem("employees-status-view") || "active");
  useEffect(() => { localStorage.setItem("employees-status-view", statusView); }, [statusView]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Effective-date prompt for historical compensation changes.
  const [effDateOpen, setEffDateOpen] = useState(false);
  const [effDate, setEffDate] = useState("");
  const [effChanges, setEffChanges] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // Per-section reveal for sensitive fields in the edit dialog. Cached
  // plaintext + which sections are currently unmasked. Unlinked to each
  // other so revealing Bank doesn't unmask SIN and vice versa.
  const [sensitiveCache, setSensitiveCache] = useState(null);
  const [showGov, setShowGov] = useState(false);
  const [showBank, setShowBank] = useState(false);
  const [revealBusy, setRevealBusy] = useState("");
  const fetchSensitiveEdit = useCallback(async () => {
    if (!editId) return null;
    if (sensitiveCache) return sensitiveCache;
    setRevealBusy("loading");
    try {
      const { data } = await api.get(`/employees/${editId}/sensitive`);
      setSensitiveCache(data || {});
      return data || {};
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot reveal");
      return null;
    } finally {
      setRevealBusy("");
    }
  }, [editId, sensitiveCache]);
  const toggleShowGov = useCallback(async () => {
    if (showGov) {
      // Hide → restore masked values from the employee record so a subsequent
      // save doesn't blank them out.
      setShowGov(false);
      const orig = employees.find((x) => x.employee_id === editId) || {};
      setForm((s) => ({ ...s, sin: orig.sin || "", license_number: orig.license_number || "" }));
      return;
    }
    const data = await fetchSensitiveEdit();
    if (!data) return;
    setForm((s) => ({ ...s, sin: data.sin || "", license_number: data.license_number || "" }));
    setShowGov(true);
  }, [showGov, fetchSensitiveEdit, employees, editId]);
  const toggleShowBank = useCallback(async () => {
    if (showBank) {
      setShowBank(false);
      const orig = employees.find((x) => x.employee_id === editId) || {};
      setForm((s) => ({ ...s, bank_transit: orig.bank_transit || "", bank_institution: orig.bank_institution || "", bank_account: orig.bank_account || "" }));
      return;
    }
    const data = await fetchSensitiveEdit();
    if (!data) return;
    setForm((s) => ({ ...s, bank_transit: data.bank_transit || "", bank_institution: data.bank_institution || "", bank_account: data.bank_account || "" }));
    setShowBank(true);
  }, [showBank, fetchSensitiveEdit, employees, editId]);
  // Reset reveal state whenever the dialog closes / edit target changes so
  // sensitive data never leaks between employees.
  useEffect(() => {
    if (!dialogOpen) {
      setSensitiveCache(null);
      setShowGov(false);
      setShowBank(false);
    }
  }, [dialogOpen]);
  const [filesFor, setFilesFor] = useState(null); // employee obj whose files dialog is open
  const [certsFor, setCertsFor] = useState(null); // employee obj whose certificates wallet is open
  const [benefitsFor, setBenefitsFor] = useState(null); // employee obj whose benefits dialog is open
  const [detailsFor, setDetailsFor] = useState(null); // employee obj whose details dialog is open
  const [pickerOpen, setPickerOpen] = useState(false); // "Files" picker across all employees
  const [cropSrc, setCropSrc] = useState(null); // data URL passed to crop dialog
  const [waiverOpen, setWaiverOpen] = useState(false);
  const confirm = useConfirm();
  const { user: currentUser } = useAuth();
  const myUserId = currentUser?.user_id;

  // Has this employee been self-edited since the current admin last saw them?
  const isPendingReview = (e) => {
    if (!e?.self_edited_at) return false;
    const seen = e.self_edit_seen_by || [];
    return !!myUserId && !seen.includes(myUserId);
  };

  const markSeenIfNeeded = async (e) => {
    if (!e || !isPendingReview(e)) return;
    try {
      const { data } = await api.post(`/employees/${e.employee_id}/mark-seen`);
      // Update local list so highlight drops off immediately.
      setEmployees((prev) => prev.map((x) => (x.employee_id === e.employee_id ? { ...x, ...data } : x)));
    } catch { /* silent — highlight will refresh on next list load */ }
  };

  useModalBack(dialogOpen, () => setDialogOpen(false));
  useModalBack(pickerOpen, () => setPickerOpen(false));
  useModalBack(!!filesFor, () => setFilesFor(null));

  const load = useCallback(async () => {
    try {
      const r = await api.get("/employees");
      setEmployees(r.data);
    } catch {
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (emp) => {
    setEditId(emp.employee_id);
    setForm({
      ...emptyForm,
      ...emp,
      pay_type: emp.pay_type || "hourly",
      hourly_rate: String(emp.hourly_rate ?? ""),
      hourly_rate_perfect: emp.hourly_rate_perfect ? String(emp.hourly_rate_perfect) : "",
      _showPerfectRate: !!emp.hourly_rate_perfect,
      weekly_salary: String(emp.weekly_salary ?? ""),
      overtime_exempt: !!emp.overtime_exempt,
      vacation_opt_out: !!emp.vacation_opt_out,
      vacation_waiver: emp.vacation_waiver || null,
      linked_role: emp.linked_role || null,
      time_clock_enabled: !!emp.time_clock_enabled,
      paid_lunch_minutes: emp.paid_lunch_minutes || 30,
      hired_at: emp.hired_at || "",
      certifications: Array.isArray(emp.certifications) ? emp.certifications : [],
      photo: emp.photo || "",
      _pendingPhotoFile: null,
      _pendingPhotoAction: null,
    });
    setDialogOpen(true);
  };

  const save = () => {
    // Tracked pay fields (hourly rate, weekly salary, overtime exempt): when
    // any of them changes on an EXISTING employee, the admin must pick the
    // date the change applies from — it lands in the historical compensation
    // log and past weeks/pay periods keep the old value.
    const orig = editId ? employees.find((x) => x.employee_id === editId) : null;
    const changes = [];
    if (orig) {
      if (Math.abs((parseFloat(form.hourly_rate) || 0) - (Number(orig.hourly_rate) || 0)) > 0.0001) changes.push("hourly rate");
      if (Math.abs((parseFloat(form.weekly_salary) || 0) - (Number(orig.weekly_salary) || 0)) > 0.0001) changes.push("weekly salary");
      if (!!form.overtime_exempt !== !!orig.overtime_exempt) changes.push("overtime exempt");
    }
    if (changes.length) {
      setEffChanges(changes);
      setEffDate("");
      setEffDateOpen(true);
      return;
    }
    doSave(null);
  };

  const doSave = async (effectiveDate) => {
    if (!form.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    // eslint-disable-next-line no-unused-vars
    const { photo: _formPhoto, _pendingPhotoFile, _pendingPhotoAction, _showPerfectRate, ...clean } = form;
    const payload = {
      ...clean,
      pay_type: form.pay_type || "hourly",
      employment_type: form.employment_type || "full_time",
      hourly_rate: parseFloat(form.hourly_rate) || 0,
      hourly_rate_perfect: parseFloat(form.hourly_rate_perfect) || 0,
      weekly_salary: parseFloat(form.weekly_salary) || 0,
      overtime_exempt: !!form.overtime_exempt,
      vacation_opt_out: !!form.vacation_opt_out,
      time_clock_enabled: !!form.time_clock_enabled,
      paid_lunch_minutes: parseInt(form.paid_lunch_minutes, 10) || 30,
      // send dates as "" (not null) so cleared values are persisted
      hired_at: form.hired_at || "",
    };
    if (effectiveDate) payload.rate_effective_date = effectiveDate;
    try {
      let empId = editId;
      if (editId) {
        await api.put(`/employees/${editId}`, payload);
      } else {
        const res = await api.post("/employees", payload);
        empId = res.data?.employee_id;
      }
      // Handle photo upload / removal after the record exists.
      if (empId && _pendingPhotoFile) {
        try {
          // Convert the File to a base64 data URL and POST as JSON. This
          // dodges reverse-proxy multipart quirks that were dropping the
          // FormData boundary in production.
          const dataUrl = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(String(r.result));
            r.onerror = reject;
            r.readAsDataURL(_pendingPhotoFile);
          });
          await api.post(`/employees/${empId}/photo`, { data_url: dataUrl });
        } catch (err) {
          console.error("photo upload failed", err?.response?.data || err);
          toast.error(err?.response?.data?.detail || "Could not save photo");
        }
      } else if (empId && _pendingPhotoAction === "remove") {
        try {
          await api.delete(`/employees/${empId}/photo`);
        } catch {
          /* ignore */
        }
      }
      toast.success(editId
        ? (effectiveDate ? `Employee updated — applies from ${effectiveDate}` : "Employee updated")
        : "Employee added");
      setDialogOpen(false);
      await load();
    } catch {
      toast.error("Could not save employee");
    } finally {
      setSaving(false);
    }
  };

  const emailEmployee = (emp) => {
    if (!emp?.email) return;
    // Navigate to Email tab; EmailTab reads ?compose_to= and opens Compose prefilled.
    const params = new URLSearchParams(window.location.search);
    params.set("tab", "email");
    params.set("compose_to", emp.email);
    params.set("compose_name", emp.name || "");
    window.history.pushState({}, "", `${window.location.pathname}?${params.toString()}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };


  const remove = async (emp) => {
    const ok = await confirm({
      title: `Remove ${emp.name}?`,
      description: `This deletes ${emp.name}'s record and every file stored under their profile. This can't be undone.`,
      confirmLabel: "Yes, remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/employees/${emp.employee_id}`);
      toast.success(`${emp.name} removed`);
      setEmployees((prev) => prev.filter((e) => e.employee_id !== emp.employee_id));
    } catch {
      toast.error("Could not remove employee");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="employees-tab">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#4f6fe8]">Directory</p>
          <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Employees</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPickerOpen(true)}
            data-testid="employees-files-btn"
            aria-label="Employee files"
            title="Employee files"
            className="flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-3 py-2.5 text-sm font-semibold text-white/80 transition-colors hover:border-white/25 hover:bg-white/10 hover:text-white"
          >
            <FolderOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Files</span>
          </button>
          <button
            onClick={openAdd}
            data-testid="add-employee-btn"
            aria-label="Add employee"
            className="flex items-center gap-2 rounded-md bg-[#1a3fd1] px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1a3fd1]/85 sm:px-4"
          >
            <UserPlus className="h-4 w-4" /> <span className="hidden sm:inline">Add employee</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center font-mono text-xs uppercase tracking-[0.2em] text-white/40">Loading…</div>
      ) : (
        <>
        <div className="mb-4 inline-flex overflow-hidden rounded-md border border-white/15 bg-white/5" data-testid="employees-status-toggle">
          <button
            type="button"
            onClick={() => setStatusView("active")}
            data-testid="employees-view-active"
            aria-pressed={statusView === "active"}
            className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest transition ${
              statusView === "active" ? "bg-[#1a3fd1] text-white" : "text-white/60 hover:text-white"
            }`}
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Active
            <span className={`ml-1 rounded px-1.5 py-0.5 font-mono text-[9px] ${statusView === "active" ? "bg-white/15" : "bg-white/5"}`}>
              {employees.filter((e) => (e.status || "Active") === "Active").length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setStatusView("inactive")}
            data-testid="employees-view-inactive"
            aria-pressed={statusView === "inactive"}
            className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest transition ${
              statusView === "inactive" ? "bg-[#1a3fd1] text-white" : "text-white/60 hover:text-white"
            }`}
          >
            <Archive className="h-3.5 w-3.5" /> Inactive
            <span className={`ml-1 rounded px-1.5 py-0.5 font-mono text-[9px] ${statusView === "inactive" ? "bg-white/15" : "bg-white/5"}`}>
              {employees.filter((e) => (e.status || "Active") !== "Active").length}
            </span>
          </button>
        </div>
        {(() => {
          const missing = employees.filter((e) => (e.status || "Active") === "Active" && !(e.hired_at || "").trim()).length;
          if (!missing) return null;
          return (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-2" data-testid="employees-missing-hire-dates">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
              <p className="text-xs text-amber-200">
                <b>{missing}</b> active employee{missing === 1 ? " is" : "s are"} missing a hire date — their vacation % and 5-year badge can't compute. Open the employee → Edit → set <b>Employed from</b>.
              </p>
            </div>
          );
        })()}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="employees-grid">
          {employees.filter((e) => statusView === "active" ? (e.status || "Active") === "Active" : (e.status || "Active") !== "Active").map((e, i) => {
            const notLinked = !e.linked_user_id;
            // A user who isn't linked can't have self-edited — skip the check.
            const pending = !notLinked && isPendingReview(e);
            const fiveYr = fiveYearDate(e.hired_at);
            const nowD = new Date();
            const fiveYrUpcoming = fiveYr && nowD < fiveYr &&
              fiveYr <= new Date(nowD.getFullYear() + 1, nowD.getMonth(), nowD.getDate());
            const hasServiceBadge = tenureFullYears(e.hired_at) >= 5 || fiveYrUpcoming;
            return (
            <motion.div
              key={e.employee_id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: Math.min(i * 0.02, 0.3) }}
              data-testid="employee-row"
              data-pending-review={pending || undefined}
              data-not-on-app={notLinked || undefined}
              onClick={() => { markSeenIfNeeded(e); setDetailsFor(e); }}
              role="button"
              tabIndex={0}
              onKeyDown={(ev) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  markSeenIfNeeded(e);
                  setDetailsFor(e);
                }
              }}
              className={`relative cursor-pointer rounded-lg border p-4 transition-colors ${pending ? "border-amber-400/60 bg-amber-400/[0.06] hover:border-amber-400 hover:bg-amber-400/[0.1]" : notLinked ? "border-red-500/60 bg-red-500/[0.06] hover:border-red-500 hover:bg-red-500/[0.1]" : "border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.05]"}`}
            >
              <div className="absolute right-2 top-2 flex items-center gap-0.5" onClick={(ev) => ev.stopPropagation()}>
                <button
                  onClick={() => setFilesFor(e)}
                  data-testid="employee-files-btn"
                  aria-label="Files"
                  className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-[#8fa4f0]"
                >
                  <FolderOpen className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setCertsFor(e)}
                  data-testid="employee-certs-btn"
                  aria-label="Certificates"
                  title="Certificates"
                  className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-amber-400"
                >
                  <Award className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setBenefitsFor(e)}
                  data-testid="employee-benefits-btn"
                  aria-label="Benefits"
                  title="Benefits — allowance, usage & old claims"
                  className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-emerald-400"
                >
                  <HeartHandshake className="h-4 w-4" />
                </button>
                <button onClick={() => { markSeenIfNeeded(e); openEdit(e); }} data-testid="edit-employee-btn" aria-label="Edit" className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-white">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(e)} data-testid="delete-employee-btn" aria-label="Delete" className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-red-400">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="absolute right-3 top-11 flex flex-col items-center gap-1" onClick={(ev) => ev.stopPropagation()}>
                <EmployeeAvatar employee={e} size={56} testid="employee-card-avatar" />
                {tenureFullYears(e.hired_at) >= 5 && (
                  <span
                    data-testid="employee-5yr-badge"
                    title={e.linked_role === "owner"
                      ? `${tenureFullYears(e.hired_at)} years of service (owners are vacation-exempt)`
                      : `${tenureFullYears(e.hired_at)} years of service — vacation pay auto-bumped to 6%`}
                    className="inline-flex items-center gap-1 rounded-full border border-yellow-500/50 bg-yellow-400/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-yellow-700 dark:text-yellow-200"
                  >
                    <Award className="h-2.5 w-2.5" /> {tenureFullYears(e.hired_at)} yrs
                  </span>
                )}
                {fiveYrUpcoming && (
                  <span
                    data-testid="employee-5yr-upcoming-badge"
                    title={`Hits 5 years on ${fiveYr.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} — vacation pay auto-bumps from 4% to 6% that week${e.linked_role === "owner" ? " (owners are vacation-exempt)" : ""}`}
                    className="inline-flex items-center gap-1 rounded-full border border-sky-500/50 bg-sky-400/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-sky-700 dark:text-sky-200"
                  >
                    <Clock className="h-2.5 w-2.5" /> 5 yrs {fiveYr.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                  </span>
                )}
              </div>

              <h3 className="pr-24 text-base font-semibold text-white/90">
                {e.name}
                {pending && (
                  <span
                    data-testid="employee-user-edited-badge"
                    title="Employee edited their own info — click to review"
                    className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-400/50 bg-amber-400/15 px-1.5 py-0.5 align-middle font-mono text-[9px] uppercase tracking-widest text-amber-200"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> user edited
                  </span>
                )}
                {notLinked && (
                  <span
                    data-testid="employee-not-on-app-badge"
                    title="This employee has no linked user account"
                    className="ml-2 inline-flex items-center gap-1 rounded-full border border-red-500/50 bg-red-500/15 px-1.5 py-0.5 align-middle font-mono text-[9px] uppercase tracking-widest text-red-300"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-red-400" /> not on app
                  </span>
                )}
                {!(e.hired_at || "").trim() && (e.status || "Active") === "Active" && (
                  <span
                    data-testid="employee-no-hire-date-badge"
                    title="No hire date — vacation % and the 5-year badge can't compute. Edit to set 'Employed from'."
                    className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-500/50 bg-amber-400/15 px-1.5 py-0.5 align-middle font-mono text-[9px] uppercase tracking-widest text-amber-700 dark:text-amber-200"
                  >
                    no hire date
                  </span>
                )}
              </h3>
              <p className="mt-0.5 text-xs text-white/50">
                {e.trade || "—"}
                <span
                  className="ml-2 rounded border border-white/15 bg-white/[0.03] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/60"
                  data-testid="employee-employment-type"
                >
                  {(e.employment_type || "full_time") === "part_time" ? "PT" : "FT"}
                </span>
              </p>

              {expiringLabels(e).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid="employee-expiry-alerts">
                  {expiringLabels(e).slice(0, 3).map((x, idx) => {
                    const expired = x.days < 0;
                    return (
                      <span
                        key={`${x.label}-${idx}`}
                        className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider ${
                          expired
                            ? "border-red-500/40 bg-red-500/10 text-red-300"
                            : "border-amber-500/40 bg-amber-500/10 text-amber-300"
                        }`}
                        title={expired ? `${x.label} expired ${-x.days} days ago` : `${x.label} expires in ${x.days} days`}
                      >
                        {x.label}: {expired ? `expired ${-x.days}d` : `${x.days}d`}
                      </span>
                    );
                  })}
                </div>
              )}

              <div className="mt-2 space-y-1 text-xs">
                {e.phone && (
                  <div className="flex items-center gap-1.5 text-white/60">
                    <Phone className="h-3.5 w-3.5 shrink-0 text-white/30" /> {e.phone}
                  </div>
                )}
                {e.email && (
                  <div className="flex items-center gap-1.5 truncate text-white/60">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-white/30" />
                    <span className="truncate">{e.email}</span>
                  </div>
                )}
                {(e.hired_at || ageFrom(e.date_of_birth) !== null) && (
                  <div className="flex items-center gap-1.5 text-white/60">
                    <Calendar className="h-3.5 w-3.5 shrink-0 text-white/30" />
                    {e.hired_at && (
                      <span data-testid="employee-tenure">Employed {tenure(e.hired_at)}</span>
                    )}
                    {ageFrom(e.date_of_birth) !== null && (
                      <span className="text-white/40" data-testid="employee-age">
                        {e.hired_at ? "· " : ""}Age {ageFrom(e.date_of_birth)}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className={`mt-3 flex flex-wrap items-center justify-between gap-2 ${hasServiceBadge ? "pr-16" : ""}`}>
                <span className={`inline-block border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${statusStyle(e.status)}`}>
                  {e.status}
                </span>
                <div className="flex items-center gap-1.5" onClick={(ev) => ev.stopPropagation()}>
                  {e.phone && (
                    <a
                      href={`tel:${(e.phone || "").replace(/[^+\d]/g, "")}`}
                      data-testid="employee-call-btn"
                      aria-label={`Call ${e.name}`}
                      title="Call"
                      className="flex h-7 w-7 items-center justify-center rounded-md border border-emerald-500/50 bg-emerald-500/15 text-emerald-700 transition-colors hover:border-emerald-500 hover:bg-emerald-500/25 dark:text-emerald-300"
                    >
                      <PhoneCall className="h-3.5 w-3.5" />
                    </a>
                  )}
                  {e.phone && (
                    <a
                      href={`sms:${(e.phone || "").replace(/[^+\d]/g, "")}`}
                      data-testid="employee-sms-btn"
                      aria-label={`Text ${e.name}`}
                      title="Text"
                      className="flex h-7 w-7 items-center justify-center rounded-md border border-orange-500/50 bg-orange-500/15 text-orange-700 transition-colors hover:border-orange-500 hover:bg-orange-500/25 dark:text-orange-300"
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                    </a>
                  )}
                  {e.email && (
                    <button
                      type="button"
                      onClick={() => emailEmployee(e)}
                      data-testid="employee-email-btn"
                      aria-label={`Email ${e.name}`}
                      title="Email"
                      className="flex h-7 w-7 items-center justify-center rounded-md border border-[#1a3fd1]/50 bg-[#1a3fd1]/15 text-[#1a3fd1] transition-colors hover:border-[#1a3fd1] hover:bg-[#1a3fd1]/25 dark:text-[#8fa4f0]"
                    >
                      <MailPlus className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <span
                    className="ml-1 font-mono text-sm text-white/80"
                    title={e.pay_type !== "salary" && e.hourly_rate_perfect
                      ? "Base rate or perfect-attendance rate (used if no away days that week)"
                      : undefined}
                    data-testid={`employee-rate-${e.employee_id}`}
                  >
                    {e.pay_type === "salary"
                      ? `${money(e.weekly_salary)}/wk`
                      : e.hourly_rate_perfect
                        ? `${money(e.hourly_rate)} or ${money(e.hourly_rate_perfect)}/hr`
                        : `${money(e.hourly_rate)}/hr`}
                  </span>
                </div>
              </div>
            </motion.div>
            );
          })}
          {employees.length === 0 && (
            <div className="col-span-full py-16 text-center text-sm text-white/40">No employees yet. Add your first team member.</div>
          )}
        </div>
        </>
      )}

      {/* add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-[#0d0d0d] text-white sm:max-w-2xl" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit employee" : "Add employee"}</DialogTitle>
            <DialogDescription className="text-white/50">Enter the team member&apos;s details.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            {/* --- Photo --- */}
            <div className="sm:col-span-2">
              <Label className="text-xs text-white/50">Employee photo</Label>
              <div className="mt-1 flex items-center gap-3">
                {form.photo ? (
                  <img
                    src={form.photo}
                    alt="preview"
                    data-testid="employee-field-photo-preview"
                    className="h-20 w-20 shrink-0 rounded-lg object-cover ring-1 ring-white/10"
                  />
                ) : (
                  <div
                    data-testid="employee-field-photo-placeholder"
                    className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-dashed border-white/15 bg-white/[0.03] font-mono text-[9px] uppercase tracking-wider text-white/40"
                  >
                    No photo
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <label
                    data-testid="employee-photo-upload-btn"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm font-medium text-white/80 transition-colors hover:border-white/25 hover:bg-white/10 hover:text-white"
                  >
                    <Upload className="h-4 w-4" />
                    {form.photo ? "Change photo" : "Upload photo"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="employee-photo-input"
                      onChange={(ev) => {
                        const f = ev.target.files?.[0];
                        if (!f) return;
                        const reader = new FileReader();
                        reader.onload = () => setCropSrc(String(reader.result));
                        reader.readAsDataURL(f);
                        ev.target.value = "";
                      }}
                    />
                  </label>
                  <label
                    data-testid="employee-photo-capture-btn"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm font-medium text-white/80 transition-colors hover:border-white/25 hover:bg-white/10 hover:text-white"
                    title="Open camera to snap a photo"
                  >
                    <Camera className="h-4 w-4" />
                    Take photo
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      data-testid="employee-photo-capture-input"
                      onChange={(ev) => {
                        const f = ev.target.files?.[0];
                        if (!f) return;
                        const reader = new FileReader();
                        reader.onload = () => setCropSrc(String(reader.result));
                        reader.readAsDataURL(f);
                        ev.target.value = "";
                      }}
                    />
                  </label>
                  {form.photo && (
                    <button
                      type="button"
                      onClick={() =>
                        setForm((s) => ({
                          ...s,
                          photo: "",
                          _pendingPhotoFile: null,
                          _pendingPhotoAction: "remove",
                        }))
                      }
                      data-testid="employee-photo-remove-btn"
                      className="inline-flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm font-medium text-red-300 transition-colors hover:border-red-500/50 hover:bg-red-500/10"
                    >
                      <X className="h-4 w-4" /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {FIELDS.map(([key, label]) => (
              <div key={key} className={key === "name" ? "sm:col-span-2" : ""}>
                <Label htmlFor={`emp-${key}`} className="text-xs text-white/50">{label}</Label>
                {key === "status" ? (
                  <select
                    id={`emp-${key}`}
                    data-testid={`employee-field-${key}`}
                    value={form[key] ?? "Active"}
                    onChange={(ev) => setForm((s) => ({ ...s, [key]: ev.target.value }))}
                    className="mt-1 h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white [color-scheme:dark]"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                ) : (
                  <Input
                    id={`emp-${key}`}
                    data-testid={`employee-field-${key}`}
                    value={form[key] ?? ""}
                    onChange={(ev) => setForm((s) => ({ ...s, [key]: ev.target.value }))}
                    className="mt-1 border-white/15 bg-white/5 text-white"
                  />
                )}
              </div>
            ))}

            <div className="sm:col-span-2">
              <Label className="text-xs text-white/50">Pay type</Label>
              <div className="mt-1 inline-flex rounded-md border border-white/15 bg-white/5 p-0.5" role="tablist" data-testid="pay-type-toggle">
                {[
                  { key: "hourly", label: "Hourly" },
                  { key: "salary", label: "Salary" },
                ].map((opt) => {
                  const active = form.pay_type === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setForm((s) => ({ ...s, pay_type: opt.key }))}
                      data-testid={`pay-type-${opt.key}`}
                      aria-pressed={active}
                      className={`rounded px-4 py-1.5 text-xs font-semibold transition-colors ${
                        active ? "bg-[#1a3fd1] text-white" : "text-white/60 hover:text-white"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs text-white/50">Employment type</Label>
              <div className="mt-1 inline-flex rounded-md border border-white/15 bg-white/5 p-0.5" role="tablist" data-testid="employment-type-toggle">
                {[
                  { key: "full_time", label: "Full-time" },
                  { key: "part_time", label: "Part-time" },
                ].map((opt) => {
                  const active = (form.employment_type || "full_time") === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setForm((s) => ({ ...s, employment_type: opt.key }))}
                      data-testid={`employment-type-${opt.key}`}
                      aria-pressed={active}
                      className={`rounded px-4 py-1.5 text-xs font-semibold transition-colors ${
                        active ? "bg-[#1a3fd1] text-white" : "text-white/60 hover:text-white"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="emp-pay-amount" className="text-xs text-white/50">
                {form.pay_type === "salary" ? "Weekly salary" : "Hourly rate"}
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  id="emp-pay-amount"
                  data-testid={form.pay_type === "salary" ? "employee-field-weekly_salary" : "employee-field-hourly_rate"}
                  value={form.pay_type === "salary" ? form.weekly_salary : form.hourly_rate}
                  onChange={(ev) => {
                    const clean = ev.target.value.replace(/[^0-9.]/g, "");
                    setForm((s) => ({ ...s, [form.pay_type === "salary" ? "weekly_salary" : "hourly_rate"]: clean }));
                  }}
                  placeholder={form.pay_type === "salary" ? "e.g. 1600" : "e.g. 34.50"}
                  className="border-white/15 bg-white/5 text-white"
                />
                {form.pay_type !== "salary" && (
                  <button
                    type="button"
                    onClick={() =>
                      setForm((s) => ({
                        ...s,
                        // Toggle: if a perfect rate exists → clear it.
                        // Otherwise open an empty second field.
                        hourly_rate_perfect: s.hourly_rate_perfect ? "" : (s.hourly_rate_perfect === "" ? " " : ""),
                        _showPerfectRate: !s._showPerfectRate,
                      }))
                    }
                    data-testid="employee-field-perfect-toggle"
                    title="Add a perfect-attendance rate"
                    aria-label="Add perfect-attendance rate"
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md border ${
                      form._showPerfectRate || form.hourly_rate_perfect
                        ? "border-red-500 bg-red-500/15 text-red-300"
                        : "border-red-500/40 bg-red-500/5 text-red-300 hover:border-red-500 hover:bg-red-500/15"
                    }`}
                  >
                    {form._showPerfectRate || form.hourly_rate_perfect ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  </button>
                )}
              </div>
              {form.pay_type !== "salary" && (form._showPerfectRate || form.hourly_rate_perfect) && (
                <div className="mt-2">
                  <Label htmlFor="emp-pay-perfect" className="text-[11px] uppercase tracking-wider text-red-300/80">
                    Perfect-attendance rate <span className="ml-1 text-white/40">— used only for weeks with zero away days</span>
                  </Label>
                  <Input
                    id="emp-pay-perfect"
                    data-testid="employee-field-hourly_rate_perfect"
                    value={form.hourly_rate_perfect}
                    onChange={(ev) => {
                      const clean = ev.target.value.replace(/[^0-9.]/g, "");
                      setForm((s) => ({ ...s, hourly_rate_perfect: clean }));
                    }}
                    placeholder="e.g. 36.00"
                    className="mt-1 border-red-500/40 bg-red-500/5 text-white focus-visible:ring-red-500/40"
                  />
                </div>
              )}
            </div>

            <div className="sm:col-span-2">
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white/80 hover:border-white/25">
                <input
                  type="checkbox"
                  checked={!!form.overtime_exempt}
                  onChange={(ev) => setForm((s) => ({ ...s, overtime_exempt: ev.target.checked }))}
                  data-testid="employee-field-overtime_exempt"
                  className="h-4 w-4 rounded border-white/30 bg-black/40 accent-[#1a3fd1]"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm">Overtime exempt</span>
                  <span className="block font-mono text-[10px] uppercase tracking-widest text-white/40">
                    Skip the ESA 1.5× rule — hours over 44 pay at straight time
                  </span>
                </span>
              </label>
            </div>

            <div className="sm:col-span-2">
              <div className="flex items-start gap-3 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2" data-testid="employee-vacation-exempt-card">
                <input
                  type="checkbox"
                  checked={form.linked_role === "owner" || !!form.vacation_opt_out}
                  readOnly
                  data-testid="employee-field-vacation_opt_out"
                  className="mt-0.5 h-4 w-4 cursor-not-allowed rounded border-white/30 bg-black/40 accent-[#1a3fd1] opacity-70"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white/90">Vacation pay opt-out</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    {form.linked_role === "owner"
                      ? "Exempt — this employee's login has the owner role"
                      : form.vacation_opt_out
                        ? `Signed waiver on file · ${(form.vacation_waiver?.signed_at || "").slice(0, 10) || "date unknown"}`
                        : "Requires a signed in-app waiver"}
                  </p>
                </div>
                {form.linked_role === "owner" ? (
                  <span className="shrink-0 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-300" data-testid="employee-vacation-exempt-badge">
                    Exempt — owner
                  </span>
                ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (!editId) {
                      toast.info("Save the employee first, then sign the waiver.");
                      return;
                    }
                    setWaiverOpen(true);
                  }}
                  data-testid="employee-open-vacation-waiver"
                  className="shrink-0 rounded-md border border-[#1a3fd1]/50 bg-[#1a3fd1]/15 px-2.5 py-1.5 text-[11px] font-semibold text-[#a9baf5] hover:border-[#1a3fd1] hover:bg-[#1a3fd1]/25 hover:text-white"
                >
                  {form.vacation_opt_out ? "View / revoke" : "Sign waiver"}
                </button>
                )}
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white/80 hover:border-white/25">
                <input
                  type="checkbox"
                  checked={!!form.time_clock_enabled}
                  onChange={(ev) => setForm((s) => ({ ...s, time_clock_enabled: ev.target.checked }))}
                  data-testid="employee-field-time_clock_enabled"
                  className="h-4 w-4 rounded border-white/30 bg-black/40 accent-[#1a3fd1]"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm">Enable in-app time clock</span>
                  <span className="block font-mono text-[10px] uppercase tracking-widest text-white/40">
                    Adds Clock In / Lunch / Clock Out on this employee&apos;s Home tab
                  </span>
                </span>
              </label>
              {form.time_clock_enabled && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">Paid lunch</span>
                  <select
                    value={form.paid_lunch_minutes || 30}
                    onChange={(ev) => setForm((s) => ({ ...s, paid_lunch_minutes: parseInt(ev.target.value, 10) }))}
                    data-testid="employee-field-paid_lunch_minutes"
                    className="rounded border border-white/15 bg-[#0d0d0d] px-2 py-1 text-sm text-white"
                  >
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 hour</option>
                  </select>
                  <span className="font-mono text-[10px] text-white/40">The clock keeps ticking through this window; anything longer is unpaid.</span>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="emp-hired-at" className="text-xs text-white/50">Employed from</Label>
              <DateInput
                id="emp-hired-at"
                testid="employee-field-hired_at"
                clearTestid="clear-hired-at"
                value={form.hired_at}
                onChange={(ev) => setForm((s) => ({ ...s, hired_at: ev.target.value }))}
                onClear={() => setForm((s) => ({ ...s, hired_at: "" }))}
              />
              {!form.hired_at && (
                <p className="mt-1 font-mono text-[10px] text-amber-300/90" data-testid="hired-at-hint">
                  Needed for the 5-year badge and the automatic 4%→6% vacation bump.
                </p>
              )}
            </div>

            {/* --- Address --- */}
            <div className="sm:col-span-2 mt-2 border-t border-white/10 pt-4">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Address</p>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="emp-street" className="text-xs text-white/50">Street</Label>
              <Input
                id="emp-street"
                data-testid="employee-field-address_street"
                value={form.address_street || ""}
                onChange={(ev) => setForm((s) => ({ ...s, address_street: ev.target.value }))}
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
            <div>
              <Label htmlFor="emp-city" className="text-xs text-white/50">City</Label>
              <Input
                id="emp-city"
                data-testid="employee-field-address_city"
                value={form.address_city || ""}
                onChange={(ev) => setForm((s) => ({ ...s, address_city: ev.target.value }))}
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
            <div>
              <Label htmlFor="emp-postal" className="text-xs text-white/50">Postal / ZIP</Label>
              <Input
                id="emp-postal"
                data-testid="employee-field-address_postal"
                value={form.address_postal || ""}
                onChange={(ev) => setForm((s) => ({ ...s, address_postal: ev.target.value }))}
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>

            {/* --- Personal --- */}
            <div className="sm:col-span-2 mt-2 border-t border-white/10 pt-4">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Personal</p>
            </div>
            <div>
              <Label htmlFor="emp-dob" className="text-xs text-white/50">Date of birth</Label>
              <DateInput
                id="emp-dob"
                testid="employee-field-date_of_birth"
                clearTestid="clear-date-of-birth"
                value={form.date_of_birth}
                onChange={(ev) => setForm((s) => ({ ...s, date_of_birth: ev.target.value }))}
                onClear={() => setForm((s) => ({ ...s, date_of_birth: "" }))}
              />
            </div>
            <div>
              <Label htmlFor="emp-shirt" className="text-xs text-white/50">Shirt / uniform size</Label>
              <Input
                id="emp-shirt"
                data-testid="employee-field-shirt_size"
                value={form.shirt_size || ""}
                onChange={(ev) => setForm((s) => ({ ...s, shirt_size: ev.target.value }))}
                placeholder="e.g. M, L, XL"
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="emp-allergies" className="text-xs text-white/50">Allergies</Label>
              <Input
                id="emp-allergies"
                data-testid="employee-field-allergies"
                value={form.allergies || ""}
                onChange={(ev) => setForm((s) => ({ ...s, allergies: ev.target.value }))}
                placeholder="e.g. penicillin, bee stings"
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>

            {/* --- Emergency contact --- */}
            <div className="sm:col-span-2 mt-2 border-t border-white/10 pt-4">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Emergency contact 1</p>
            </div>
            <div>
              <Label htmlFor="emp-ec-name" className="text-xs text-white/50">Name</Label>
              <Input
                id="emp-ec-name"
                data-testid="employee-field-emergency_contact_name"
                value={form.emergency_contact_name || ""}
                onChange={(ev) => setForm((s) => ({ ...s, emergency_contact_name: ev.target.value }))}
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
            <div>
              <Label htmlFor="emp-ec-rel" className="text-xs text-white/50">Relationship</Label>
              <Input
                id="emp-ec-rel"
                data-testid="employee-field-emergency_contact_relationship"
                value={form.emergency_contact_relationship || ""}
                onChange={(ev) => setForm((s) => ({ ...s, emergency_contact_relationship: ev.target.value }))}
                placeholder="e.g. Spouse, Parent"
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="emp-ec-phone" className="text-xs text-white/50">Phone</Label>
              <Input
                id="emp-ec-phone"
                data-testid="employee-field-emergency_contact_phone"
                value={form.emergency_contact_phone || ""}
                onChange={(ev) => setForm((s) => ({ ...s, emergency_contact_phone: ev.target.value }))}
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>

            {/* --- Emergency contact 2 --- */}
            <div className="sm:col-span-2 mt-2 border-t border-white/10 pt-4">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Emergency contact 2</p>
            </div>
            <div>
              <Label htmlFor="emp-ec2-name" className="text-xs text-white/50">Name</Label>
              <Input
                id="emp-ec2-name"
                data-testid="employee-field-emergency_contact_2_name"
                value={form.emergency_contact_2_name || ""}
                onChange={(ev) => setForm((s) => ({ ...s, emergency_contact_2_name: ev.target.value }))}
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
            <div>
              <Label htmlFor="emp-ec2-rel" className="text-xs text-white/50">Relationship</Label>
              <Input
                id="emp-ec2-rel"
                data-testid="employee-field-emergency_contact_2_relationship"
                value={form.emergency_contact_2_relationship || ""}
                onChange={(ev) => setForm((s) => ({ ...s, emergency_contact_2_relationship: ev.target.value }))}
                placeholder="e.g. Sibling, Friend"
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="emp-ec2-phone" className="text-xs text-white/50">Phone</Label>
              <Input
                id="emp-ec2-phone"
                data-testid="employee-field-emergency_contact_2_phone"
                value={form.emergency_contact_2_phone || ""}
                onChange={(ev) => setForm((s) => ({ ...s, emergency_contact_2_phone: ev.target.value }))}
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>

            {/* --- Government & License --- */}
            <div className="sm:col-span-2 mt-2 border-t border-white/10 pt-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Government &amp; License</p>
                {editId && (
                  <button
                    type="button"
                    onClick={toggleShowGov}
                    disabled={!!revealBusy}
                    data-testid="employee-reveal-gov-btn"
                    className="flex items-center gap-1 rounded border border-white/15 bg-white/5 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white/70 hover:border-white/25 hover:text-white"
                  >
                    {showGov ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {revealBusy ? "…" : (showGov ? "Hide" : "Reveal to edit")}
                  </button>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="emp-sin" className="text-xs text-white/50">SIN / SSN</Label>
              <Input
                id="emp-sin"
                data-testid="employee-field-sin"
                value={form.sin || ""}
                onChange={(ev) => setForm((s) => ({ ...s, sin: ev.target.value }))}
                readOnly={!!editId && !showGov}
                onFocus={() => { if (editId && !showGov) toggleShowGov(); }}
                placeholder="123-456-789"
                className={`mt-1 border-white/15 bg-white/5 font-mono ${editId && !showGov ? "cursor-pointer text-white/50" : "text-white"}`}
              />
            </div>
            <div>
              <Label htmlFor="emp-lic" className="text-xs text-white/50">Driver&apos;s license #</Label>
              <Input
                id="emp-lic"
                data-testid="employee-field-license_number"
                value={form.license_number || ""}
                onChange={(ev) => setForm((s) => ({ ...s, license_number: ev.target.value }))}
                readOnly={!!editId && !showGov}
                onFocus={() => { if (editId && !showGov) toggleShowGov(); }}
                placeholder={editId && !showGov ? "tap Reveal to edit" : ""}
                className={`mt-1 border-white/15 bg-white/5 font-mono ${editId && !showGov ? "cursor-pointer text-white/50" : "text-white"}`}
              />
            </div>
            <div>
              <Label htmlFor="emp-lic-exp" className="text-xs text-white/50">License expiry</Label>
              <DateInput
                id="emp-lic-exp"
                testid="employee-field-license_expiry"
                clearTestid="clear-license-expiry"
                value={form.license_expiry}
                onChange={(ev) => setForm((s) => ({ ...s, license_expiry: ev.target.value }))}
                onClear={() => setForm((s) => ({ ...s, license_expiry: "" }))}
              />
            </div>

            {/* --- Certifications / Tickets --- */}
            <div className="sm:col-span-2 mt-2 border-t border-white/10 pt-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Certifications / Tickets</p>
                <button
                  type="button"
                  onClick={() =>
                    setForm((s) => ({ ...s, certifications: [...(s.certifications || []), { name: "", expiry: "" }] }))
                  }
                  data-testid="cert-add-btn"
                  className="rounded border border-white/15 px-2 py-1 text-xs text-white/70 hover:border-white/30 hover:text-white"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2">
                {(form.certifications || []).map((cert, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`cert-row-${idx}`}>
                    <Input
                      value={cert.name || ""}
                      placeholder="e.g. WHMIS, Working at Heights"
                      onChange={(ev) =>
                        setForm((s) => {
                          const next = [...(s.certifications || [])];
                          next[idx] = { ...next[idx], name: ev.target.value };
                          return { ...s, certifications: next };
                        })
                      }
                      data-testid={`cert-name-${idx}`}
                      className="flex-1 border-white/15 bg-white/5 text-white"
                    />
                    <div className="relative w-44">
                      <Input
                        type="date"
                        value={cert.expiry || ""}
                        onChange={(ev) =>
                          setForm((s) => {
                            const next = [...(s.certifications || [])];
                            next[idx] = { ...next[idx], expiry: ev.target.value };
                            return { ...s, certifications: next };
                          })
                        }
                        data-testid={`cert-expiry-${idx}`}
                        className="border-white/15 bg-white/5 pr-8 text-white [color-scheme:dark]"
                      />
                      {cert.expiry ? (
                        <button
                          type="button"
                          onClick={() =>
                            setForm((s) => {
                              const next = [...(s.certifications || [])];
                              next[idx] = { ...next[idx], expiry: "" };
                              return { ...s, certifications: next };
                            })
                          }
                          data-testid={`cert-expiry-clear-${idx}`}
                          aria-label="Clear expiry"
                          title="Clear date"
                          className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-white/50 hover:bg-white/10 hover:text-red-400"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setForm((s) => ({
                          ...s,
                          certifications: (s.certifications || []).filter((_, i) => i !== idx),
                        }))
                      }
                      data-testid={`cert-remove-${idx}`}
                      aria-label="Remove"
                      className="rounded p-2 text-white/50 hover:bg-white/10 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {(!form.certifications || form.certifications.length === 0) && (
                  <p className="text-xs text-white/40">No certifications added yet.</p>
                )}
              </div>
            </div>

            {/* --- Direct deposit --- */}
            <div className="sm:col-span-2 mt-2 border-t border-white/10 pt-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Direct deposit</p>
                {editId && (
                  <button
                    type="button"
                    onClick={toggleShowBank}
                    disabled={!!revealBusy}
                    data-testid="employee-reveal-bank-btn"
                    className="flex items-center gap-1 rounded border border-white/15 bg-white/5 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white/70 hover:border-white/25 hover:text-white"
                  >
                    {showBank ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {revealBusy ? "…" : (showBank ? "Hide" : "Reveal to edit")}
                  </button>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="emp-bank-transit" className="text-xs text-white/50">Transit #</Label>
              <Input
                id="emp-bank-transit"
                data-testid="employee-field-bank_transit"
                value={form.bank_transit || ""}
                onChange={(ev) => setForm((s) => ({ ...s, bank_transit: ev.target.value.replace(/[^0-9]/g, "") }))}
                readOnly={!!editId && !showBank}
                onFocus={() => { if (editId && !showBank) toggleShowBank(); }}
                inputMode="numeric"
                placeholder="5 digits"
                className={`mt-1 border-white/15 bg-white/5 font-mono ${editId && !showBank ? "cursor-pointer text-white/50" : "text-white"}`}
              />
            </div>
            <div>
              <Label htmlFor="emp-bank-inst" className="text-xs text-white/50">Institution #</Label>
              <Input
                id="emp-bank-inst"
                data-testid="employee-field-bank_institution"
                value={form.bank_institution || ""}
                onChange={(ev) => setForm((s) => ({ ...s, bank_institution: ev.target.value.replace(/[^0-9]/g, "") }))}
                readOnly={!!editId && !showBank}
                onFocus={() => { if (editId && !showBank) toggleShowBank(); }}
                inputMode="numeric"
                placeholder="3 digits"
                className={`mt-1 border-white/15 bg-white/5 font-mono ${editId && !showBank ? "cursor-pointer text-white/50" : "text-white"}`}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="emp-bank-acct" className="text-xs text-white/50">Account #</Label>
              <Input
                id="emp-bank-acct"
                data-testid="employee-field-bank_account"
                value={form.bank_account || ""}
                onChange={(ev) => setForm((s) => ({ ...s, bank_account: ev.target.value.replace(/[^0-9]/g, "") }))}
                readOnly={!!editId && !showBank}
                onFocus={() => { if (editId && !showBank) toggleShowBank(); }}
                inputMode="numeric"
                placeholder="7–12 digits"
                className={`mt-1 border-white/15 bg-white/5 font-mono ${editId && !showBank ? "cursor-pointer text-white/50" : "text-white"}`}
              />
            </div>

            {/* --- Notes --- */}
            <div className="sm:col-span-2 mt-2 border-t border-white/10 pt-4">
              <Label htmlFor="emp-notes" className="text-xs text-white/50">Notes</Label>
              <Textarea
                id="emp-notes"
                data-testid="employee-field-notes"
                rows={3}
                value={form.notes || ""}
                onChange={(ev) => setForm((s) => ({ ...s, notes: ev.target.value }))}
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/20 bg-transparent text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid="employee-save-btn" className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Effective-date prompt — shown when a tracked pay field (hourly
          rate, weekly salary, overtime exempt) changes on an existing
          employee. No default date: the admin must pick. */}
      <Dialog open={effDateOpen} onOpenChange={setEffDateOpen}>
        <DialogContent className="max-w-md" data-testid="effective-date-dialog">
          <DialogHeader>
            <DialogTitle>When does this change apply?</DialogTitle>
            <DialogDescription>
              You changed: <span className="font-semibold text-white">{effChanges.join(", ")}</span>.
              Pick the date the new value takes effect. Weeks and pay periods before
              that date keep the old value — the change is saved to the employee&apos;s
              historical compensation log.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="eff-date" className="font-mono text-[10px] uppercase tracking-widest text-white/60">
              Applies from
            </Label>
            <Input
              id="eff-date"
              type="date"
              value={effDate}
              onChange={(ev) => setEffDate(ev.target.value)}
              data-testid="effective-date-input"
              className="mt-1 border-white/15 bg-white/5 text-white"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEffDateOpen(false)} data-testid="effective-date-cancel"
              className="border-white/20 bg-transparent text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!effDate) { toast.error("Pick the date this change applies from"); return; }
                setEffDateOpen(false);
                doSave(effDate);
              }}
              data-testid="effective-date-save"
              className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85"
            >
              Save change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PhotoCropDialog
        open={!!cropSrc}
        src={cropSrc}
        onCancel={() => setCropSrc(null)}
        onConfirm={(file, dataUrl) => {
          setForm((s) => ({
            ...s,
            photo: dataUrl,
            _pendingPhotoFile: file,
            _pendingPhotoAction: null,
          }));
          setCropSrc(null);
        }}
      />

      <VacationWaiverDialog
        open={waiverOpen}
        employee={editId ? { ...form, employee_id: editId } : null}
        onClose={() => setWaiverOpen(false)}
        onSaved={(emp) => {
          // Merge the server's canonical waiver + opt_out flags back into
          // the form so the status card updates without closing the outer
          // employee dialog. Also refresh the list.
          setForm((s) => ({
            ...s,
            vacation_opt_out: !!emp.vacation_opt_out,
            vacation_waiver: emp.vacation_waiver || null,
          }));
          load();
        }}
      />

      {certsFor && (
        <AdminCertificatesDialog employee={certsFor} onClose={() => setCertsFor(null)} />
      )}
      {benefitsFor && (
        <BenefitsDialog employee={benefitsFor} onClose={() => setBenefitsFor(null)} />
      )}
      {filesFor && (
        <EmployeeFilesDialog
          employee={filesFor}
          onClose={() => setFilesFor(null)}
        />
      )}

      <EmployeeDetailsDialog
        employee={detailsFor}
        onOpenChange={(v) => (v ? null : setDetailsFor(null))}
        onEdit={(emp) => {
          setDetailsFor(null);
          openEdit(emp);
        }}
      />

      {/* Cross-employee file picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-h-[80vh] overflow-hidden border-white/10 bg-[#0b0b0f] text-white sm:max-w-lg" data-testid="employee-files-picker">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-[#8fa4f0]" /> Employee files
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Pick an employee to open their files.
            </DialogDescription>
          </DialogHeader>
          <EmployeeFilesPicker
            employees={employees}
            onPick={(emp) => {
              setPickerOpen(false);
              setFilesFor(emp);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------- Employee Files Explorer ----------------
const EmployeeFilesPicker = ({ employees, onPick }) => {
  const [q, setQ] = useState("");
  const filtered = (employees || []).filter((e) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (
      (e.name || "").toLowerCase().includes(s) ||
      (e.job_title || "").toLowerCase().includes(s) ||
      (e.trade || "").toLowerCase().includes(s)
    );
  });
  return (
    <div className="space-y-3 pt-1">
      <Input
        autoFocus
        value={q}
        onChange={(ev) => setQ(ev.target.value)}
        placeholder="Search employees…"
        data-testid="employee-picker-search"
        className="border-white/15 bg-white/5 text-white"
      />
      <div className="max-h-[55vh] overflow-y-auto rounded-md border border-white/10">
        {filtered.length === 0 ? (
          <p className="p-4 text-center text-sm text-white/50">No employees match.</p>
        ) : (
          <ul className="divide-y divide-white/5">
            {filtered.map((e) => (
              <li key={e.employee_id}>
                <button
                  type="button"
                  onClick={() => onPick(e)}
                  data-testid={`employee-picker-row-${e.employee_id}`}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white/5"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white/5 text-[#8fa4f0]">
                    <FolderOpen className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white/90">{e.name}</p>
                    <p className="truncate text-xs text-white/50">
                      {[e.job_title, e.trade].filter(Boolean).join(" · ") || "—"}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-white/40" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const EmployeeFilesDialog = ({ employee, onClose }) => {
  const confirm = useConfirm();
  const [folderId, setFolderId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [view, setView] = useState("list"); // list | grid-sm | grid-lg
  const [busy, setBusy] = useState(false);
  const [menuFor, setMenuFor] = useState(null); // {type:'file'|'folder', id}
  const [selected, setSelected] = useState(() => new Set()); // file_ids selected
  const [thumbs, setThumbs] = useState({}); // file_id -> blob url (images only)
  const [movePickerOpen, setMovePickerOpen] = useState(false);
  const [allFolders, setAllFolders] = useState([]);
  const inputRef = useRef(null);

  const load = useCallback(async (fid) => {
    try {
      const r = await api.get(`/employees/${employee.employee_id}/browse`, { params: { folder_id: fid || "" } });
      setFolderId(r.data.folder_id || null);
      setBreadcrumb(r.data.breadcrumb || []);
      setFolders(r.data.folders || []);
      setFiles(r.data.files || []);
      setSelected(new Set());
    } catch {
      toast.error("Could not load files");
    }
  }, [employee.employee_id]);

  useEffect(() => { load(null); }, [load]);

  // Lazily fetch image thumbnails for image files in view
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const f of files) {
        if (!f.mime_type?.startsWith("image/") || thumbs[f.file_id]) continue;
        try {
          const r = await api.get(`/employees/${employee.employee_id}/files/${f.file_id}/download`, { responseType: "blob" });
          if (cancelled) return;
          const url = URL.createObjectURL(r.data);
          setThumbs((prev) => ({ ...prev, [f.file_id]: url }));
        } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, employee.employee_id]);

  const toggleSelect = (fid) => setSelected((prev) => { const n = new Set(prev); n.has(fid) ? n.delete(fid) : n.add(fid); return n; });
  const clearSelection = () => setSelected(new Set());

  const openMovePicker = async () => {
    try {
      const r = await api.get(`/employees/${employee.employee_id}/all-folders`);
      setAllFolders(r.data?.folders || []);
      setMovePickerOpen(true);
    } catch { toast.error("Could not load folders"); }
  };

  const moveSelectedTo = async (targetFolderId) => {
    const ids = Array.from(selected);
    setMovePickerOpen(false);
    try {
      await Promise.all(ids.map((fid) => api.patch(`/employees/${employee.employee_id}/files/${fid}`, { folder_id: targetFolderId || "" })));
      toast.success(`Moved ${ids.length} file${ids.length > 1 ? "s" : ""}`);
      clearSelection();
      await load(folderId);
    } catch { toast.error("Move failed"); }
  };

  const doUpload = async (ev) => {
    const chosen = Array.from(ev.target.files || []);
    if (chosen.length === 0) return;
    setBusy(true);
    const fd = new FormData();
    chosen.forEach((f) => fd.append("files", f));
    if (folderId) fd.append("folder_id", folderId);
    try {
      await api.post(`/employees/${employee.employee_id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${chosen.length}`);
      await load(folderId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const newFolder = async () => {
    const name = window.prompt("Folder name?");
    if (!name) return;
    try {
      await api.post(`/employees/${employee.employee_id}/folders`, { parent_id: folderId, name });
      await load(folderId);
    } catch { toast.error("Could not create folder"); }
  };

  const renameFolder = async (f) => {
    const name = window.prompt("New folder name", f.name);
    if (!name || name === f.name) return;
    try {
      await api.patch(`/employees/${employee.employee_id}/folders/${f.folder_id}`, { name });
      await load(folderId);
    } catch { toast.error("Rename failed"); }
  };

  const deleteFolder = async (f) => {
    const ok = await confirm({
      title: `Delete folder "${f.name}"?`,
      description: "Every file and subfolder inside will be permanently removed. This can't be undone.",
      confirmLabel: "Delete folder",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/employees/${employee.employee_id}/folders/${f.folder_id}`);
      await load(folderId);
    } catch { toast.error("Delete failed"); }
  };

  const renameFile = async (f) => {
    const name = window.prompt("New file name", f.name);
    if (!name || name === f.name) return;
    try {
      await api.patch(`/employees/${employee.employee_id}/files/${f.file_id}`, { name });
      await load(folderId);
    } catch { toast.error("Rename failed"); }
  };

  const deleteFile = async (f) => {
    const ok = await confirm({
      title: `Delete "${f.name}"?`,
      description: "This file will be permanently removed from the employee's folder.",
      confirmLabel: "Delete file",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/employees/${employee.employee_id}/files/${f.file_id}`);
      await load(folderId);
    } catch { toast.error("Delete failed"); }
  };

  const openFile = async (f) => {
    try {
      const r = await api.get(`/employees/${employee.employee_id}/files/${f.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open file"); }
  };

  const downloadFile = async (f) => {
    try {
      const r = await api.get(`/employees/${employee.employee_id}/files/${f.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const prettySize = (n) => !n ? "" : n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

  const gridTile = view === "grid-lg" ? "h-32 w-32" : "h-24 w-24";
  const gridIcon = view === "grid-lg" ? "h-12 w-12" : "h-9 w-9";
  const isGrid = view.startsWith("grid");

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0d0d0d] text-white sm:max-w-3xl" data-testid="employee-files-dialog">
        <DialogHeader>
          <DialogTitle>Files — {employee.name}</DialogTitle>
          <DialogDescription className="text-white/50">
            Only files and folders belonging to {employee.name} are shown here.
          </DialogDescription>
        </DialogHeader>

        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-white/10 pb-3">
          <Button onClick={newFolder} data-testid="new-folder-btn" size="sm" variant="outline" className="border-white/15 bg-transparent text-white/80 hover:bg-white/10">
            <FolderPlus className="mr-1.5 h-4 w-4" /> New folder
          </Button>
          <input ref={inputRef} type="file" multiple className="hidden" onChange={doUpload} data-testid="employee-file-input" />
          <Button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="employee-file-upload-btn" size="sm" className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85">
            <Upload className="mr-1.5 h-4 w-4" /> {busy ? "Uploading…" : "Upload"}
          </Button>
          <div className="ml-auto inline-flex overflow-hidden rounded-md border border-white/15" data-testid="view-toggle">
            {[
              { k: "list", Icon: ListIcon, label: "List" },
              { k: "grid-sm", Icon: LayoutList, label: "Small" },
              { k: "grid-lg", Icon: LayoutGrid, label: "Large" },
            ].map(({ k, Icon }) => (
              <button
                key={k}
                onClick={() => setView(k)}
                data-testid={`view-${k}`}
                aria-pressed={view === k}
                className={`px-2.5 py-1.5 ${view === k ? "bg-[#1a3fd1] text-white" : "bg-transparent text-white/60 hover:bg-white/10"}`}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>

        {/* breadcrumb */}
        <div className="flex flex-wrap items-center gap-1 text-xs text-white/60" data-testid="breadcrumb">
          <button onClick={() => load(null)} className="rounded px-2 py-0.5 hover:bg-white/10">
            {employee.name}
          </button>
          {breadcrumb.map((c) => (
            <span key={c.folder_id} className="flex items-center">
              <ChevronRight className="h-3.5 w-3.5 text-white/30" />
              <button onClick={() => load(c.folder_id)} className="rounded px-2 py-0.5 hover:bg-white/10">
                {c.name}
              </button>
            </span>
          ))}
        </div>

        {/* selection toolbar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-2 rounded-md border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 px-3 py-2 text-xs" data-testid="selection-bar">
            <span className="text-white/80">{selected.size} selected</span>
            <button onClick={openMovePicker} data-testid="move-selected-btn" className="ml-auto flex items-center gap-1 rounded-md bg-[#1a3fd1] px-3 py-1 font-semibold text-white hover:bg-[#1a3fd1]/85">
              <MoveRight className="h-3.5 w-3.5" /> Move to folder
            </button>
            <button onClick={clearSelection} className="rounded-md border border-white/15 px-2 py-1 text-white/70 hover:text-white">Cancel</button>
          </div>
        )}

        {/* items */}
        <div className="max-h-[55vh] min-h-[200px] overflow-y-auto rounded-lg border border-white/10 p-2" onClick={() => setMenuFor(null)}>
          {folders.length === 0 && files.length === 0 ? (
            <p className="py-10 text-center text-sm text-white/40">This folder is empty. Use &ldquo;New folder&rdquo; or &ldquo;Upload&rdquo;.</p>
          ) : isGrid ? (
            <div className="flex flex-wrap gap-3">
              {folders.map((f) => (
                <Tile key={f.folder_id} testid="folder-tile" size={gridTile} isFolder
                      icon={<Folder className={`${gridIcon} text-[#8fa4f0]`} />}
                      label={f.name}
                      sub={typeof f.file_count === "number" ? `${f.file_count} file${f.file_count === 1 ? "" : "s"}` : undefined}
                      onClick={() => load(f.folder_id)}
                      onMenu={(e) => { e.stopPropagation(); setMenuFor({ type: "folder", id: f.folder_id }); }}
                      menu={menuFor?.type === "folder" && menuFor.id === f.folder_id && (
                        <ItemMenu items={[["Rename", () => renameFolder(f)], ["Delete", () => deleteFolder(f), true]]} />
                      )} />
              ))}
              {files.map((f) => {
                const thumb = thumbs[f.file_id];
                const isImg = f.mime_type?.startsWith("image/");
                return (
                  <Tile key={f.file_id} testid="file-tile" size={gridTile} label={f.name} sub={prettySize(f.size)}
                        selected={selected.has(f.file_id)}
                        onToggleSelect={() => toggleSelect(f.file_id)}
                        icon={
                          isImg && thumb
                            ? <img src={thumb} alt="" className="max-h-full max-w-full rounded object-cover" />
                            : <FileIcon className={`${gridIcon} text-white/50`} />
                        }
                        onClick={() => (selected.size > 0 ? toggleSelect(f.file_id) : openFile(f))}
                        onMenu={(e) => { e.stopPropagation(); setMenuFor({ type: "file", id: f.file_id }); }}
                        menu={menuFor?.type === "file" && menuFor.id === f.file_id && (
                          <ItemMenu items={[["Open", () => openFile(f)], ["Download", () => downloadFile(f)], ["Rename", () => renameFile(f)], ["Delete", () => deleteFile(f), true]]} />
                        )} />
                );
              })}
            </div>
          ) : (
            <ul className="divide-y divide-white/10">
              {folders.map((f) => (
                <li key={f.folder_id} className="flex items-center gap-2 px-2 py-2 text-sm" data-testid="folder-row">
                  <button onClick={() => load(f.folder_id)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                    <Folder className="h-4 w-4 shrink-0 text-[#8fa4f0]" />
                    <span className="truncate text-white/90 hover:underline">{f.name}</span>
                    {typeof f.file_count === "number" && (
                      <span
                        className="ml-1 shrink-0 rounded bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-white/50"
                        data-testid="folder-file-count"
                      >
                        {f.file_count} file{f.file_count === 1 ? "" : "s"}
                      </span>
                    )}
                  </button>
                  <button onClick={() => renameFolder(f)} aria-label="Rename" data-testid="folder-rename" className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-white"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => deleteFolder(f)} aria-label="Delete folder" data-testid="folder-delete" className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-red-400"><X className="h-4 w-4" /></button>
                </li>
              ))}
              {files.map((f) => (
                <li key={f.file_id} className={`flex items-center gap-2 px-2 py-2 text-sm ${selected.has(f.file_id) ? "bg-[#1a3fd1]/15" : ""}`} data-testid="employee-file-row">
                  <input type="checkbox" checked={selected.has(f.file_id)} onChange={() => toggleSelect(f.file_id)} data-testid="file-select" className="h-4 w-4 shrink-0 accent-[#1a3fd1]" />
                  <button onClick={() => (selected.size > 0 ? toggleSelect(f.file_id) : openFile(f))} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                    {thumbs[f.file_id] ? (
                      <img src={thumbs[f.file_id]} alt="" className="h-6 w-6 shrink-0 rounded object-cover" />
                    ) : (
                      <FileIcon className="h-4 w-4 shrink-0 text-white/50" />
                    )}
                    <span className="truncate text-white/90 hover:underline">{f.name}</span>
                    <span className="ml-2 shrink-0 font-mono text-[10px] text-white/40">{prettySize(f.size)}</span>
                  </button>
                  <button onClick={() => renameFile(f)} aria-label="Rename" data-testid="file-rename" className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-white"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => downloadFile(f)} aria-label="Download" data-testid="employee-file-download" className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-white"><Download className="h-4 w-4" /></button>
                  <button onClick={() => deleteFile(f)} aria-label="Delete" data-testid="employee-file-delete" className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-red-400"><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {movePickerOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setMovePickerOpen(false)}>
            <div className="w-full max-w-sm rounded-lg border border-white/10 bg-[#141414] p-4" onClick={(e) => e.stopPropagation()} data-testid="move-folder-picker">
              <h4 className="mb-3 text-sm font-semibold text-white">Move {selected.size} file{selected.size > 1 ? "s" : ""} to…</h4>
              <div className="max-h-64 overflow-y-auto rounded-md border border-white/10">
                <button onClick={() => moveSelectedTo(null)} className="block w-full px-3 py-2 text-left text-xs text-white/80 hover:bg-white/10">
                  <Folder className="mr-2 inline h-4 w-4 text-[#8fa4f0]" /> {employee.name} (root)
                </button>
                {allFolders.map((f) => (
                  <button key={f.folder_id} onClick={() => moveSelectedTo(f.folder_id)} data-testid="move-target" className="block w-full px-3 py-2 text-left text-xs text-white/80 hover:bg-white/10">
                    <Folder className="mr-2 inline h-4 w-4 text-[#8fa4f0]" /> {f.path}
                  </button>
                ))}
                {allFolders.length === 0 && (
                  <p className="px-3 py-4 text-center text-xs text-white/40">No subfolders yet. Create one first.</p>
                )}
              </div>
              <div className="mt-3 text-right">
                <button onClick={() => setMovePickerOpen(false)} className="text-xs text-white/60 hover:text-white">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Tile = ({ size, icon, label, sub, onClick, onMenu, menu, testid }) => (
  <div data-testid={testid} className={`relative flex ${size} flex-col items-center justify-start gap-1 rounded-lg border border-white/10 bg-white/[0.03] p-2 hover:border-white/25`}>
    <button onClick={onClick} className="flex flex-1 flex-col items-center justify-center gap-1">
      {icon}
      <span className="line-clamp-2 w-full break-words text-center text-[11px] text-white/80">{label}</span>
      {sub && <span className="text-[10px] text-white/40">{sub}</span>}
    </button>
    <button onClick={onMenu} aria-label="More" className="absolute right-1 top-1 rounded p-0.5 text-white/50 hover:bg-white/10 hover:text-white">
      <MoreVertical className="h-3.5 w-3.5" />
    </button>
    {menu}
  </div>
);

const ItemMenu = ({ items }) => (
  <div onClick={(e) => e.stopPropagation()} className="absolute right-1 top-6 z-10 min-w-[130px] overflow-hidden rounded-md border border-white/10 bg-[#141414] text-xs shadow-lg" data-testid="tile-menu">
    {items.map(([label, fn, danger]) => (
      <button key={label} onClick={fn} className={`block w-full px-3 py-1.5 text-left hover:bg-white/10 ${danger ? "text-red-400" : "text-white/80"}`}>
        {label}
      </button>
    ))}
  </div>
);

export default EmployeesTab;
